import * as React from 'react';

import {Button} from 'shells/button/button';
import {Link} from 'shells/link/link';
import {MfaCode} from 'shells/mfa/mfa-code';
import {NavButtons} from 'shells/modal/step-modal/layout';
import {Header} from 'shells/typography/header';
import {Subheader} from 'shells/typography/subheader';

import {TYPE_RECOVERY, TYPE_SMS, TYPE_APP} from '../../../setup/mfa/constants';
import type {GetMfaApiResponse} from '../../../setup/mfa/types';

import {mfa, getMfa, isMobileAuth, loginWithAuthToken, getMobileUdid} from '../../utils';

import styles from '../login-form.module.css';

type Step = 'code' | 'recovery';

type Props = {
    onSubmit: (formValues: any) => undefined | Promise<any>;
    mfaType?: string;
    onBack: () => void;
};

export function MFACodeForm(props: Props) {
    const [step, setStep] = React.useState<Step>('code');
    const [mfaValue, setMfaValue] = React.useState<string>('');
    const [codeIsResent, setCodeIsResent] = React.useState<boolean>(false);
    const {mfaType} = props;

    const isMobile = isMobileAuth();

    React.useEffect(() => {
        getMfa().then((result: GetMfaApiResponse) => {
            if (mfaType === 'phone' && result.result && result.result.value) {
                setMfaValue(result.result.value);
            }
        });
    }, [mfaType, setMfaValue]);

    const handleVerifyCode = (code: string, type: string): Promise<any> => {
        return new Promise(function (resolve, reject) {
            if (isMobile) {
                return mfa({
                    code: code,
                    type: type,
                    udid: getMobileUdid(),
                    isMobile: true,
                })
                    .then((response) => response.json())
                    .then((data) => {
                        if (data && data.authToken) {
                            loginWithAuthToken(data.username, data.authToken);
                            // @ts-expect-error
                            resolve();
                        } else {
                            reject(data);
                        }
                    });
            }

            mfa({
                code: code,
                type: type,
            }).then((result) => {
                if (result.redirected) {
                    // Success, will redirect
                    document.location.href = result.url;
                    // @ts-expect-error
                    resolve();
                } else {
                    // Invalid code
                    // @ts-expect-error
                    result.json().then((body) => {
                        reject(body);
                    });
                }
            });
        });
    };

    if (step === 'code') {
        return (
            <form onSubmit={props.onSubmit}>
                <Header>Two-factor authentication</Header>
                <Subheader>
                    {props.mfaType === 'phone'
                        ? `We texted your code to your phone number ending in ${mfaValue}`
                        : 'Generate a code with your authenticator app'}
                </Subheader>
                <div className='mt-24'>
                    <MfaCode
                        onVerifyMfaCode={(code) =>
                            handleVerifyCode(code, mfaType === 'phone' ? TYPE_SMS : TYPE_APP)
                        }
                        inputCount={6}
                        isMobile={isMobile}
                    />
                </div>
                {props.mfaType === 'phone' ? (
                    <div className={styles.resend}>
                        Didn’t get a code?&nbsp;
                        <div>
                            {codeIsResent ? (
                                <div>Sent!</div>
                            ) : (
                                <Button
                                    variant='text-primary'
                                    // @ts-expect-error
                                    disabled={codeIsResent}
                                    size='normal'
                                    onClick={() => {
                                        setCodeIsResent(true);

                                        Nut.ajax.post({
                                            url: '/auth/mfa-resend-phone-code',
                                        });

                                        // Re-enable button after 5 seconds
                                        const timer = setTimeout(() => {
                                            setCodeIsResent(false);
                                        }, 5000);

                                        return () => clearTimeout(timer);
                                    }}
                                >
                                    Send a new one
                                </Button>
                            )}
                        </div>
                    </div>
                ) : null}
                <div className={styles['recovery-button-container']}>
                    <Button
                        size='normal'
                        // @ts-expect-error
                        onClick={() => {
                            setStep('recovery');
                        }}
                    >
                        Use recovery code
                    </Button>
                </div>
                <NavButtons style={{justifyContent: 'flex-start', margin: 0}}>
                    <Link
                        variant='primary'
                        size='big'
                        onClick={() => {
                            props.onBack();
                        }}
                    >
                        Back
                    </Link>
                </NavButtons>
            </form>
        );
    } else if (step === 'recovery') {
        return (
            <>
                <Header>Recovery code</Header>
                <Subheader>Enter one of your recovery codes to verify your identity</Subheader>

                <div className='mt-24'>
                    <MfaCode
                        onVerifyMfaCode={(code) => handleVerifyCode(code, TYPE_RECOVERY)}
                        inputCount={6}
                        isMobile={isMobile}
                    />
                </div>

                <NavButtons style={{justifyContent: 'flex-start'}}>
                    <Link
                        variant='primary'
                        size='big'
                        onClick={() => {
                            setStep('code');
                        }}
                    >
                        Back
                    </Link>
                </NavButtons>
            </>
        );
    } else {
        return null;
    }
}
